import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { SiteFooter } from "../components/SiteFooter";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Car, Plus, Trash2, CheckCircle2, Clock, XCircle, CreditCard, Ticket, Loader2, Ban, RefreshCw, RotateCw } from "lucide-react";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

function StatusBadge({ status }) {
  const map = {
    active: { c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", i: CheckCircle2, t: "Active" },
    expired: { c: "text-amber-400 border-amber-500/40 bg-amber-500/10", i: Clock, t: "Expired" },
    inactive: { c: "text-muted-foreground border-white/15 bg-white/5", i: XCircle, t: "No Membership" },
  };
  const m = map[status] || map.inactive;
  const I = m.i;
  return <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 overline ${m.c}`}><I className="w-3.5 h-3.5" /> {m.t}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [plate, setPlate] = useState("");
  const [nickname, setNickname] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [v, t] = await Promise.all([api.get("/vehicles"), api.get("/wash/tickets")]);
    setVehicles(v.data); setTickets(t.data);
  };
  useEffect(() => { load(); }, []);

  const addVehicle = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/vehicles", { plate, nickname: nickname || null });
      setPlate(""); setNickname("");
      toast.success("Vehicle added");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const removeVehicle = async (id) => {
    try { await api.delete(`/vehicles/${id}`); toast.success("Vehicle removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const subscribe = async (vehicleId) => {
    setBusy(vehicleId + "-pay");
    try {
      const res = await api.post("/payments/checkout", { vehicle_id: vehicleId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(null); }
  };

  const getTicket = async (vehicleId) => {
    setBusy(vehicleId + "-ticket");
    try {
      const res = await api.post("/wash/ticket", { vehicle_id: vehicleId });
      toast.success(`Wash ticket ${res.data.code} issued`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const cancelSub = async (vehicleId) => {
    try { await api.post(`/vehicles/${vehicleId}/cancel`); toast.success("Auto-renewal cancelled — active until period end"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resumeSub = async (vehicleId) => {
    try { await api.post(`/vehicles/${vehicleId}/resume`); toast.success("Auto-renewal resumed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const monthlyTotal = vehicles.filter((v) => v.auto_renew && !v.cancelled).reduce((s, v) => s + v.price, 0);

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="overline text-volt mb-2">Member Dashboard</div>
        <h1 className="font-head font-extrabold text-4xl uppercase tracking-tight mb-10">Hi, {user?.name}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Vehicles */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-head font-bold text-2xl uppercase tracking-tight flex items-center gap-2"><Car className="w-5 h-5 text-volt" /> My Cars</h2>
              {monthlyTotal > 0 && (
                <div data-testid="monthly-total" className="text-right">
                  <div className="overline text-muted-foreground">Auto-Renews Monthly</div>
                  <div className="font-head font-black text-2xl text-volt">${monthlyTotal.toFixed(2)} <span className="text-sm text-muted-foreground">AUD/mo</span></div>
                </div>
              )}
            </div>

            <div className="space-y-4" data-testid="vehicle-list">
              {vehicles.length === 0 && (
                <div className="border border-dashed border-white/15 p-8 text-center text-muted-foreground text-sm">
                  No cars yet. Add your number plate below to get started.
                </div>
              )}
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`vehicle-${v.plate}`} className="border border-white/10 bg-surface p-5 hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="inline-flex items-center bg-white text-black plate font-bold text-2xl px-3 py-1 border-2 border-black">{v.plate}</div>
                      {v.nickname && <div className="text-sm text-muted-foreground mt-2">{v.nickname}</div>}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <StatusBadge status={v.status} />
                        <span className="overline text-muted-foreground">${v.price.toFixed(2)}/mo</span>
                      </div>
                      {v.current_period_end && (
                        <div className="overline text-muted-foreground mt-2">
                          {v.active && v.auto_renew && !v.cancelled && `Auto-renews ${fmtDate(v.current_period_end)}`}
                          {v.active && v.cancelled && `Ends ${fmtDate(v.current_period_end)} · auto-renew off`}
                          {!v.active && `Expired ${fmtDate(v.current_period_end)}`}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-stretch">
                      {v.active ? (
                        <>
                          <button data-testid={`get-ticket-${v.plate}`} onClick={() => getTicket(v.id)} disabled={busy === v.id + "-ticket"}
                            className="flex items-center justify-center gap-2 bg-volt text-white px-4 py-2.5 overline hover:bg-blue-600 transition-colors disabled:opacity-60">
                            {busy === v.id + "-ticket" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Get Wash Ticket
                          </button>
                          {v.cancelled ? (
                            <button data-testid={`resume-${v.plate}`} onClick={() => resumeSub(v.id)}
                              className="flex items-center justify-center gap-2 border border-white/15 px-4 py-2.5 overline text-muted-foreground hover:text-emerald-400 hover:border-emerald-500/40 transition-colors">
                              <RotateCw className="w-4 h-4" /> Resume Auto-Renew
                            </button>
                          ) : (
                            <button data-testid={`cancel-${v.plate}`} onClick={() => cancelSub(v.id)}
                              className="flex items-center justify-center gap-2 border border-white/15 px-4 py-2.5 overline text-muted-foreground hover:text-amber-400 hover:border-amber-500/40 transition-colors">
                              <Ban className="w-4 h-4" /> Cancel Auto-Renew
                            </button>
                          )}
                        </>
                      ) : (
                        <button data-testid={`subscribe-${v.plate}`} onClick={() => subscribe(v.id)} disabled={busy === v.id + "-pay"}
                          className="flex items-center justify-center gap-2 bg-volt text-white px-4 py-2.5 overline hover:bg-blue-600 transition-colors disabled:opacity-60">
                          {busy === v.id + "-pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Subscribe ${v.price.toFixed(2)}
                        </button>
                      )}
                      <button data-testid={`remove-${v.plate}`} onClick={() => removeVehicle(v.id)}
                        className="flex items-center justify-center gap-2 border border-white/15 px-4 py-2.5 overline text-muted-foreground hover:text-red-400 hover:border-red-500/40 transition-colors">
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add form */}
            <form onSubmit={addVehicle} className="mt-6 border border-white/10 bg-surface p-5">
              <div className="overline text-muted-foreground mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-volt" /> Add A Car</div>
              <p className="text-xs text-muted-foreground mb-4 tracking-wide">First plate $49.95/mo · each extra plate 10% off ($44.96/mo). Memberships auto-renew monthly until you cancel.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="add-plate-input" required placeholder="NUMBER PLATE" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  className="bg-input border border-white/10 px-4 py-3 plate uppercase text-white focus:outline-none focus:ring-2 focus:ring-volt" />
                <input data-testid="add-nickname-input" placeholder="Nickname (optional)" value={nickname} onChange={(e) => setNickname(e.target.value)}
                  className="bg-input border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-volt" />
              </div>
              <button data-testid="add-vehicle-btn" disabled={adding} type="submit"
                className="mt-4 flex items-center gap-2 bg-white text-black px-6 py-3 font-head font-bold uppercase tracking-wide hover:bg-white/90 transition-colors disabled:opacity-60">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Car
              </button>
            </form>
          </div>

          {/* Tickets */}
          <div>
            <h2 className="font-head font-bold text-2xl uppercase tracking-tight flex items-center gap-2 mb-4"><Ticket className="w-5 h-5 text-volt" /> Wash History</h2>
            <div className="border border-white/10 bg-surface" data-testid="ticket-history">
              {tickets.length === 0 && <div className="p-6 text-sm text-muted-foreground">No wash tickets yet.</div>}
              {tickets.map((t) => (
                <div key={t.id} className="p-4 border-b border-white/10 last:border-b-0 flex items-center justify-between">
                  <div>
                    <div className="font-head font-bold text-lg">{t.code}</div>
                    <div className="overline text-muted-foreground">{t.plate} · {new Date(t.issued_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</div>
                  </div>
                  <span className={`overline px-2 py-1 border ${t.status === "redeemed" ? "text-muted-foreground border-white/15" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"}`}>{t.status}</span>
                </div>
              ))}
            </div>
            <Link to="/wash" data-testid="dashboard-wash-link" className="mt-6 block text-center border border-white/15 px-6 py-4 overline hover:border-volt hover:text-white transition-colors">
              Go To Wash Check-In
            </Link>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
